/**
 * Renders Backlog-flavoured text inline, inflating `![image][filename]`
 * tokens into actual `<img>` tags. Backlog stores attachments out of
 * band — the body text only carries the filename — so we proxy the
 * bytes through `fetchBacklogAttachment` and inline them as data URLs.
 *
 * Why data URLs (not blob URLs / Storage cache):
 *   - The callable already roundtrips base64; pasting it straight into
 *     `src` saves a Blob hop.
 *   - The WBS panel renders < 10 distinct images per task in practice;
 *     the in-memory cost is negligible against the simpler lifecycle.
 *   - A future Firebase Storage cache can ship later without changing
 *     this component's contract.
 *
 * Caching: the module-scope promise cache dedupes concurrent /
 * remount fetches for the same `(projectId, issueKey, name)` triple.
 * Each session-scoped cache entry stays alive until the tab is closed
 * — Backlog filenames are immutable so staleness isn't a concern.
 */
import { useEffect, useState } from 'react';
import {
  fetchBacklogAttachment,
  type FetchBacklogAttachmentResult,
} from '../../lib/backlogAttachment';

/**
 * Backlog image syntax. The doc syntax is `![image][filename.ext]` and
 * the filename may include spaces or Japanese characters but not a
 * closing bracket — so the lazy `[^\]]+` body is safe.
 *
 * Exported for the unit tests.
 */
export const BACKLOG_IMAGE_TOKEN_REGEX = /!\[image\]\[([^\]]+)\]/g;

interface Token {
  kind: 'text' | 'image';
  value: string;
}

/**
 * Split a Backlog-flavoured body into text and image tokens, preserving
 * the original order. Exported for the unit tests.
 */
export function tokenizeBacklogBody(text: string): Token[] {
  const tokens: Token[] = [];
  let lastIndex = 0;
  const regex = new RegExp(BACKLOG_IMAGE_TOKEN_REGEX.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ kind: 'text', value: text.slice(lastIndex, match.index) });
    }
    tokens.push({ kind: 'image', value: match[1]! });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    tokens.push({ kind: 'text', value: text.slice(lastIndex) });
  }
  return tokens;
}

type CacheEntry = Promise<FetchBacklogAttachmentResult>;
const cache = new Map<string, CacheEntry>();

function cacheKey(projectId: string, issueKey: string, name: string): string {
  return `${projectId}/${issueKey}/${name}`;
}

/** Test seam: drop the in-flight / resolved promise cache. */
export function clearBacklogImageCacheForTests(): void {
  cache.clear();
}

export interface BacklogImageContentProps {
  projectId: string;
  issueKey: string;
  text: string;
}

export function BacklogImageContent({
  projectId,
  issueKey,
  text,
}: BacklogImageContentProps): JSX.Element {
  const tokens = tokenizeBacklogBody(text);
  // `min-w-0` lets this block shrink inside a flex / grid parent (the
  // panel body is a flex column); without it long unbreakable runs and
  // wide <img> can push the panel into horizontal scroll on mobile.
  // `overflow-wrap: anywhere` (Tailwind: `[overflow-wrap:anywhere]`)
  // breaks long English / URL tokens that `break-words` leaves alone.
  return (
    <div
      className="w-full min-w-0 overflow-x-hidden whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm text-slate-800"
      data-testid="backlog-image-content"
    >
      {tokens.map((t, i) => {
        if (t.kind === 'text') {
          return (
            <span key={i} data-testid="backlog-image-content-text">
              {t.value}
            </span>
          );
        }
        return (
          <BacklogImage
            key={i}
            projectId={projectId}
            issueKey={issueKey}
            name={t.value}
          />
        );
      })}
    </div>
  );
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; contentType: string; base64: string }
  | { kind: 'error' };

function BacklogImage({
  projectId,
  issueKey,
  name,
}: {
  projectId: string;
  issueKey: string;
  name: string;
}): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const key = cacheKey(projectId, issueKey, name);
    let promise = cache.get(key);
    if (!promise) {
      promise = fetchBacklogAttachment({
        projectId,
        issueKey,
        attachmentName: name,
      });
      cache.set(key, promise);
    }
    promise.then(
      (res) => {
        if (cancelled) return;
        if (res.ok) {
          setState({ kind: 'ok', contentType: res.contentType, base64: res.base64 });
        } else {
          setState({ kind: 'error' });
        }
      },
      () => {
        if (cancelled) return;
        // Drop the rejected promise so a remount can retry rather than
        // re-resolve to the same error forever.
        cache.delete(key);
        setState({ kind: 'error' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [projectId, issueKey, name]);

  if (state.kind === 'loading') {
    return (
      <span
        className="text-xs text-slate-500"
        data-testid="backlog-image-loading"
      >
        [画像読み込み中: {name}]
      </span>
    );
  }
  if (state.kind === 'error') {
    return (
      <span
        className="text-xs text-slate-500"
        data-testid="backlog-image-error"
      >
        ![image][{name}]
      </span>
    );
  }
  // `block` so the image consumes its own row (Backlog comments tend to
  // be a heading line + screenshot + caption); `h-auto` keeps the
  // aspect ratio when the column is narrower than the natural width
  // (the default `height: <attr>` would otherwise stretch). `max-h-[60vh]`
  // is a sanity cap so a 4000×6000px screenshot doesn't dominate the
  // panel — users can scroll the panel body to see more.
  return (
    <img
      src={`data:${state.contentType};base64,${state.base64}`}
      alt={name}
      loading="lazy"
      decoding="async"
      className="my-1 block h-auto max-h-[60vh] w-auto max-w-full rounded border border-slate-200 object-contain"
      data-testid="backlog-image"
    />
  );
}
